/**
 * @module
 *
 * @import {Controller} from "@acng/frontend-stargazer"
 *
 * @todo
 *
 *   - Improve URL search parameters
 */

import {COMPILE, extend, UPGRADE} from '@acng/frontend-stargazer';
import {on, CHANGE} from '@acng/frontend-bounty/dom/event.js';
import {TYPE} from '@acng/frontend-bounty/dom/attribute.js';
import {URLSearchParams} from '@acng/frontend-bounty/net/url.js';
import {each, reduce} from '@acng/frontend-bounty/std/array.js';
import {split} from '@acng/frontend-bounty/std/string.js';
import {BOOLEAN, ENUM, guard, IS, OBJECT, RECORD} from '@acng/frontend-rubicon';
import {SubmitEventController, RemoveController} from 'nova';

import {get, put} from '../../core/service/backend.js';

/**
 * The mailing config controller is a singleton.
 */
const mailingConfigController = (() => {
  // DOM Dictionary
  const $elements = 'elements';
  const $checkbox = 'checkbox';
  const $checked = 'checked';
  const $requestSubmit = 'requestSubmit';
  const $type = TYPE;

  const INFORMER = 'informer';
  const NEWSLETTER = 'newsletter';
  const BONUS = 'bonus';
  const TRANSACTION = 'transaction';
  const MARKETING = 'marketing';

  /**
   * @type {[TRANSACTION, ...MARKETING_GROUP]}
   */
  const CONFIG_NAME = [TRANSACTION, INFORMER, NEWSLETTER, BONUS];

  /**
   * @type {[INFORMER, NEWSLETTER, BONUS]}
   */
  const MARKETING_GROUP = [INFORMER, NEWSLETTER, BONUS];

  /**
   * @type {[...CONFIG_NAME, MARKETING]}
   */
  const CHECKBOX_NAME = [...CONFIG_NAME, MARKETING];

  /**
   * @typedef FormControls
   *
   * @property {HTMLInputElement} marketing
   * @property {HTMLInputElement} informer
   * @property {HTMLInputElement} newsletter
   * @property {HTMLInputElement} bonus
   * @property {HTMLInputElement} transaction
   */

  /**
   * @typedef GetConfigData
   *
   * @property {boolean} transaction_subscribed
   * @property {boolean} informer_subscribed
   * @property {boolean} bonus_subscribed
   * @property {boolean} newsletter_subscribed
   * @property {boolean} unsubscribed
   * @property {boolean} bounced
   */

  const UNSUBSCRIBED = 'unsubscribed';
  const BOUNCED = 'bounced';
  const AVAILABLE = 'available';

  /**
   * @typedef PutConfigData
   *
   * @property {boolean} [transaction_subscribed]
   * @property {boolean} [informer_subscribed]
   * @property {boolean} [bonus_subscribed]
   * @property {boolean} [newsletter_subscribed]
   * @property {string | null} subscription - User id as string
   * @property {string | null} email
   * @property {string | null} hash
   */

  /**
   * @template {CONFIG_NAME[number]} T
   *
   * @param {T} name
   *
   * @returns {`${T}_subscribed`}
   */
  const configKey = (name) => `${name}_subscribed`;

  /**
   * @example
   *
   * ```html
   * <!--@ ${auth.settings.mail.config} -->
   * <form>
   *   <!--@ ${unsubscribed} -->
   *   <strong>Unsubscribed</strong>
   *
   *   <!--@ ${bounced} -->
   *   <strong>Bounced</strong>
   *
   *   <input name="marketing" />
   *   <input name="informer" />
   *   <input name="newsletter" />
   *   <input name="bonus" />
   *
   *   <input name="transaction" />
   * </form>
   * ```
   *
   * @implements {Controller}
   */
  class MailingConfigController extends SubmitEventController {
    [BOUNCED] = new RemoveController(false);
    [UNSUBSCRIBED] = new RemoveController(false);
    [AVAILABLE] = new RemoveController(true);

    /**
     * @param {HTMLFormElement} form
     */
    async submit(form) {
      ASSERT: {
        guard(form.elements, RECORD(ENUM(CHECKBOX_NAME), IS(HTMLInputElement)));
        guard(this.#config, VALID_CONFIG_DATA);
      }

      const input = form[$elements];
      const config = this.#config;

      // Prepare data object with changed input values.
      const putData = reduce(
        CONFIG_NAME,
        (putData, name) => {
          if (input[name][$checked] !== config[configKey(name)]) {
            putData[configKey(name)] = config[configKey(name)] = input[name][$checked];
          }
          return putData;
        },
        this.#putData,
      );

      try {
        await putConfig(form, putData);
      } catch (error) {
        // Try to get valid data again.
        await this.#getConfig(form);
        this.#restoreCheckmarks(input);
      }
    }

    /**
     * @returns {URLSearchParams}
     */
    get #urlParams() {
      return new URLSearchParams(split(location.hash, '?')[1]);
    }

    /**
     * @returns {PutConfigData}
     */
    get #putData() {
      const params = this.#urlParams;

      return {
        subscription: params.get('subscription'), // userId
        email: params.get('email'),
        hash: params.get('hash'),
      };
    }

    /**
     * @param {FormControls} input
     */
    #restoreCheckmarks(input) {
      ASSERT: guard(this.#config, VALID_CONFIG_DATA);
      const config = this.#config;

      each(CONFIG_NAME, (name) => {
        input[name][$checked] = config[configKey(name)];
      });
      this.#restoreMarketingCheckmark(input);
    }

    /**
     * 1. Force each config input type to be "checkbox".
     * 2. Restore checkmarks according to the config.
     * 3. Add change event listeners.
     *
     * @param {Element} form
     * @param {Comment | Text | Attr} _node
     * @param {import('@acng/frontend-stargazer').InternalEngine} engine
     */
    [UPGRADE](form, _node, engine) {
      extend(engine, this);
      ASSERT: guard(form, IS(HTMLFormElement));
      const input = form[$elements];
      ASSERT: guard(input, RECORD(ENUM(CHECKBOX_NAME), IS(HTMLInputElement), true));

      each(CHECKBOX_NAME, (name) => (input[name][$type] = $checkbox));
      this.#restoreCheckmarks(input);

      on(form, CHANGE, () => {
        this.#restoreMarketingCheckmark(input);
        form[$requestSubmit]();
      });

      on(input[MARKETING], CHANGE, () => {
        each(MARKETING_GROUP, (name) => {
          input[name][$checked] = input[MARKETING][$checked];
        });
      });
    }

    /**
     * Fetch the config from the API.
     *
     * @param {Element} form
     */
    async [COMPILE](form) {
      await this.#getConfig(form);
    }

    /**
     * @type {GetConfigData | undefined}
     */
    #config;

    /**
     * @param {Element} element
     */
    async #getConfig(element) {
      const ctrl = this;
      const url = 'user/mail-config';
      const config = await get(`${url}?${ctrl.#urlParams}`, element);
      ASSERT: guard(config, VALID_CONFIG_DATA);

      ctrl.#config = config;
      ctrl[AVAILABLE].condition = !config[UNSUBSCRIBED] && !config[BOUNCED];
      ctrl[UNSUBSCRIBED].condition = config[UNSUBSCRIBED];
      ctrl[BOUNCED].condition = config[BOUNCED];
    }

    /**
     * Check the {@link MARKETING} input only if at least one of the
     * {@link MARKETING_GROUP} is checked.
     *
     * @param {FormControls} input
     *
     * @returns {void}
     */
    #restoreMarketingCheckmark(input) {
      input[MARKETING][$checked] = reduce(
        MARKETING_GROUP,
        (checked, name) => checked || input[name][$checked],
        false,
      );
    }
  }

  /**
   * @param {Element} element
   * @param {PutConfigData} data
   *
   * @returns {Promise<void>}
   */
  const putConfig = (element, data) => put(`user/mail-config`, data, element);

  return new MailingConfigController();
})();

const VALID_CONFIG_DATA = /* @__PURE__ */ OBJECT({
  transaction_subscribed: BOOLEAN,
  informer_subscribed: BOOLEAN,
  bonus_subscribed: BOOLEAN,
  newsletter_subscribed: BOOLEAN,
  unsubscribed: BOOLEAN,
  bounced: BOOLEAN,
});

export {mailingConfigController};
